import { ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import type { HelpTicket } from '../../../services/helpService';
import type { ExpandedRows } from './useHelpdeskData';
import { formatTimestamp, getPriorityStyle, getStatusStyle } from './utils';

interface HelpdeskTableProps {
  filteredTickets: HelpTicket[];
  loading: boolean;
  expandedRows: ExpandedRows;
  toggleRowExpansion: (ticketId: string) => void;
  handleTicketClick: (ticket: HelpTicket) => void;
}

export const HelpdeskTable: React.FC<HelpdeskTableProps> = ({
  filteredTickets,
  loading,
  expandedRows,
  toggleRowExpansion,
  handleTicketClick,
}) => {
  const theme = useTheme();

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: '0.9rem', fontWeight: 600 }}>Ticket</TableCell>
              <TableCell sx={{ fontSize: '0.9rem', fontWeight: 600 }}>Subject</TableCell>
              <TableCell sx={{ fontSize: '0.9rem', fontWeight: 600 }}>User</TableCell>
              <TableCell sx={{ fontSize: '0.9rem', fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontSize: '0.9rem', fontWeight: 600 }}>Priority</TableCell>
              <TableCell sx={{ fontSize: '0.9rem', fontWeight: 600 }}>Category</TableCell>
              <TableCell sx={{ fontSize: '0.9rem', fontWeight: 600 }}>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTickets.map((ticket: HelpTicket) => (
              <React.Fragment key={ticket.id}>
                <TableRow
                  hover
                  onClick={() => handleTicketClick(ticket)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                    borderLeft:
                      ticket.lastResponseAuthorType === 'user'
                        ? `4px solid ${theme.palette.bidlogiq.orange}`
                        : 'none',
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRowExpansion(ticket.id);
                        }}
                      >
                        {expandedRows[ticket.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      <Typography variant="body2" sx={{ ml: 1, fontSize: '0.85rem' }}>
                        {ticket.ticketNumber}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={ticket.title}>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.85rem',
                        }}
                      >
                        {ticket.title}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.95rem' }}>
                      {ticket.userName && ticket.userName !== 'N/A'
                        ? ticket.userName
                        : ticket.userEmail}
                    </Typography>
                    <Typography variant="caption" color="text.primary" sx={{ fontSize: '0.85rem' }}>
                      {ticket.userEmail}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ticket.status}
                      size="small"
                      sx={{ ...getStatusStyle(theme, ticket.status), fontSize: '0.85rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ticket.priority}
                      size="small"
                      sx={{ ...getPriorityStyle(theme, ticket.priority), fontSize: '0.85rem' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.85rem' }}>{ticket.category}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      {formatTimestamp(ticket.createdAt)}
                    </Typography>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={expandedRows[ticket.id] || false} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 1 }}>
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{ fontSize: '0.85rem' }}
                        >
                          <strong>Responses:</strong> {ticket.responseCount} •
                          <strong> Last Updated:</strong> {formatTimestamp(ticket.updatedAt)}
                        </Typography>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredTickets.length === 0 && !loading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No tickets found matching the current filters.
        </Alert>
      )}
    </>
  );
};
